/**
 * Device Fingerprinting Utility
 *
 * Generates a semi-unique device fingerprint based on browser/device characteristics.
 * Used for trial abuse prevention - helps detect multiple accounts from same device.
 *
 * Note: This is NOT meant to be 100% unique or unbypassable - it's a deterrent
 * that catches casual abuse attempts. Sophisticated attackers can bypass this.
 */

interface FingerprintComponents {
  userAgent: string
  language: string
  colorDepth: number
  screenResolution: string
  timezone: string
  timezoneOffset: number
  sessionStorage: boolean
  localStorage: boolean
  indexedDB: boolean
  cpuClass: string | undefined
  platform: string
  plugins: string
  canvas: string
  webgl: string
  webglVendor: string
  adBlock: boolean
  hasLiedLanguages: boolean
  hasLiedResolution: boolean
  hasLiedOs: boolean
  hasLiedBrowser: boolean
  touchSupport: string
  fonts: string
  audio: string
}

// Simple hash function (djb2)
function hashString(str: string): string {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i)
    hash = hash & hash // Convert to 32bit integer
  }
  // Convert to hex and ensure positive
  return Math.abs(hash).toString(16).padStart(8, '0')
}

// Get canvas fingerprint
function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return 'no-canvas'

    canvas.width = 200
    canvas.height = 50

    // Draw text with various styles
    ctx.textBaseline = 'top'
    ctx.font = '14px Arial'
    ctx.fillStyle = '#f60'
    ctx.fillRect(125, 1, 62, 20)
    ctx.fillStyle = '#069'
    ctx.fillText('Dealigent FP', 2, 15)
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
    ctx.fillText('Dealigent FP', 4, 17)

    return hashString(canvas.toDataURL())
  } catch {
    return 'canvas-error'
  }
}

// Get WebGL fingerprint
function getWebGLFingerprint(): { renderer: string; vendor: string } {
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
    if (!gl) return { renderer: 'no-webgl', vendor: 'no-webgl' }

    const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info')
    if (!debugInfo) return { renderer: 'no-debug-info', vendor: 'no-debug-info' }

    const vendor = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown'
    const renderer = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown'

    return { renderer, vendor }
  } catch {
    return { renderer: 'webgl-error', vendor: 'webgl-error' }
  }
}

// Get installed plugins (for non-modern browsers)
function getPlugins(): string {
  try {
    const plugins: string[] = []
    for (let i = 0; i < navigator.plugins.length; i++) {
      plugins.push(navigator.plugins[i].name)
    }
    return plugins.sort().join(',')
  } catch {
    return 'no-plugins'
  }
}

// Get touch support info
function getTouchSupport(): string {
  const maxTouchPoints = navigator.maxTouchPoints || 0
  const touchEvent = 'ontouchstart' in window
  return `${maxTouchPoints}-${touchEvent}`
}

// Detect basic ad blocker
function detectAdBlock(): boolean {
  try {
    const ad = document.createElement('div')
    ad.innerHTML = '&nbsp;'
    ad.className = 'adsbox'
    ad.style.position = 'absolute'
    ad.style.left = '-9999px'
    document.body.appendChild(ad)
    const blocked = ad.offsetHeight === 0
    document.body.removeChild(ad)
    return blocked
  } catch {
    return false
  }
}

// Audio context fingerprint
function getAudioFingerprint(): string {
  try {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext
    if (!AudioContext) return 'no-audio'

    const context = new AudioContext()
    const oscillator = context.createOscillator()
    const analyser = context.createAnalyser()
    const gainNode = context.createGain()
    const scriptProcessor = context.createScriptProcessor(4096, 1, 1)

    gainNode.gain.value = 0
    oscillator.type = 'triangle'
    oscillator.connect(analyser)
    analyser.connect(scriptProcessor)
    scriptProcessor.connect(gainNode)
    gainNode.connect(context.destination)

    oscillator.start(0)

    const fingerprint = hashString(context.sampleRate.toString() + analyser.fftSize.toString())

    oscillator.stop()
    context.close()

    return fingerprint
  } catch {
    return 'audio-error'
  }
}

/**
 * Generate device fingerprint
 * Returns a hex string that uniquely identifies this browser/device combination
 */
export async function generateDeviceFingerprint(): Promise<string> {
  const webgl = getWebGLFingerprint()

  const components: FingerprintComponents = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    colorDepth: window.screen.colorDepth,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    sessionStorage: !!window.sessionStorage,
    localStorage: !!window.localStorage,
    indexedDB: !!window.indexedDB,
    cpuClass: (navigator as any).cpuClass,
    platform: navigator.platform,
    plugins: getPlugins(),
    canvas: getCanvasFingerprint(),
    webgl: webgl.renderer,
    webglVendor: webgl.vendor,
    adBlock: detectAdBlock(),
    hasLiedLanguages: false,
    hasLiedResolution: false,
    hasLiedOs: false,
    hasLiedBrowser: false,
    touchSupport: getTouchSupport(),
    fonts: 'not-collected', // Font enumeration is slow and invasive
    audio: getAudioFingerprint()
  }

  // Create a stable string from all components
  const fingerprintString = Object.entries(components)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${value}`)
    .join('|')

  // Generate final hash
  const hash = hashString(fingerprintString)

  // Add a prefix to identify this as our fingerprint format
  return `DFP-${hash.toUpperCase()}`
}

/**
 * Get cached fingerprint or generate new one
 * Caches in sessionStorage to avoid recalculating
 */
let cachedFingerprint: string | null = null

export async function getDeviceFingerprint(): Promise<string> {
  if (cachedFingerprint) {
    return cachedFingerprint
  }

  // Try to get from sessionStorage first
  try {
    const stored = sessionStorage.getItem('dfp')
    if (stored) {
      cachedFingerprint = stored
      return stored
    }
  } catch {
    // sessionStorage not available
  }

  // Generate new fingerprint
  const fingerprint = await generateDeviceFingerprint()
  cachedFingerprint = fingerprint

  // Store in sessionStorage for this session
  try {
    sessionStorage.setItem('dfp', fingerprint)
  } catch {
    // sessionStorage not available
  }

  return fingerprint
}

/**
 * Clear cached fingerprint (useful for testing)
 */
export function clearFingerprintCache(): void {
  cachedFingerprint = null
  try {
    sessionStorage.removeItem('dfp')
  } catch {
    // sessionStorage not available
  }
}
